import { Router } from 'express';
import { db } from '../storage';
import { PaymentOrder } from '../../src/types';

export const sepayRouter = Router();

// Helper to build VietQR link with SePay syntax
function buildVietQrUrl(bank: string, acc: string, amount: number, content: string, template: string = 'compact2') {
  const cleanContent = encodeURIComponent(content.trim());
  return `https://img.vietqr.io/image/${bank}-${acc}-${template}.png?amount=${amount}&addInfo=${cleanContent}`;
}

// Live SePay API Query Helper
async function checkSePayApiForOrder(order: PaymentOrder): Promise<boolean> {
  const settings = db.getSystemSettings();
  const apiKey = settings.sepayConfig?.apiKey;
  const accountNumber = settings.sepayConfig?.accountNumber;
  if (!apiKey) return false;

  try {
    const queryUrl = `https://my.sepay.vn/userapi/transactions/list?account_number=${encodeURIComponent(accountNumber || '')}&limit=25`;
    const response = await fetch(queryUrl, {
      headers: {
        'Authorization': `Apikey ${apiKey}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      return false;
    }

    const data: any = await response.json();
    const transactions = data?.transactions || data?.messages || [];
    if (Array.isArray(transactions)) {
      const orderCodeUpper = order.orderCode.toUpperCase();
      const orderIdUpper = (order.id || '').toUpperCase();

      const matched = transactions.find((t: any) => {
        const desc = (t.transaction_content || t.content || t.description || t.code || '').toUpperCase();
        const amountIn = Number(t.amount_in || t.transferAmount || t.amount || 0);

        const hasCode = desc.includes(orderCodeUpper) || (orderIdUpper && desc.includes(orderIdUpper));
        const hasAmount = order.amount === 0 || amountIn >= (order.amount * 0.95);
        return hasCode && hasAmount;
      });

      if (matched) {
        db.activatePaymentOrder(order.id, matched.id ? String(matched.id) : `SEPAY_API_${Date.now()}`);
        return true;
      }
    }
  } catch (err) {
    console.error('Error querying SePay transactions list:', err);
  }
  return false;
}

// POST /api/sepay/create-order - Create subscription / renewal payment order
sepayRouter.post('/create-order', (req, res) => {
  const {
    teacherId,
    teacherName,
    teacherEmail,
    teacherPassword,
    teacherPhone,
    teacherSubject,
    planId,
    isRenewal
  } = req.body;

  let targetName = teacherName;
  let targetEmail = teacherEmail;
  let targetPhone = teacherPhone;
  let targetSubject = teacherSubject;

  if (teacherId) {
    const existingTeacher = db.getUserById(teacherId);
    if (existingTeacher) {
      targetName = targetName || existingTeacher.name;
      targetEmail = targetEmail || existingTeacher.email;
      targetPhone = targetPhone || existingTeacher.phone;
      targetSubject = targetSubject || existingTeacher.subject;
    }
  }

  if (!targetName || !targetEmail || !planId) {
    return res.status(400).json({ error: 'Vui lòng cung cấp đầy đủ thông tin Họ tên, Email và Gói dịch vụ' });
  }

  const settings = db.getSystemSettings();
  const plan = settings.pricingPlans.find(p => p.id === planId) || settings.pricingPlans[0];

  const orderCode = `TM${Date.now().toString().slice(-6)}`;
  const amount = plan.price;
  const sepayConf = settings.sepayConfig;

  // Build VietQR image URL
  const qrUrl = buildVietQrUrl(
    sepayConf.bankName || 'BIDV',
    sepayConf.accountNumber || '96247219319',
    amount,
    orderCode
  );

  const order = db.createPaymentOrder({
    orderCode,
    teacherName: targetName,
    teacherEmail: targetEmail,
    teacherPassword: teacherPassword || '123456',
    teacherPhone: targetPhone || '',
    teacherSubject: targetSubject || 'Giáo viên dạy thêm',
    planId: plan.id,
    planName: isRenewal ? `[Gia Hạn] ${plan.name}` : plan.name,
    durationMonths: plan.durationMonths,
    amount,
    qrUrl,
    status: 'pending'
  });

  res.json({
    success: true,
    order,
    bankInfo: {
      bankName: sepayConf.bankName || 'BIDV',
      accountNumber: sepayConf.accountNumber || '96247219319',
      accountHolder: sepayConf.accountHolder || 'NGUYEN VAN QUAN TRI',
      orderCode,
      amount,
      qrUrl
    }
  });
});

// GET /api/sepay/check-order/:orderId - Check if order is paid
sepayRouter.get('/check-order/:orderId', async (req, res) => {
  const order = db.getPaymentOrder(req.params.orderId);
  if (!order) {
    return res.status(404).json({ error: 'Không tìm thấy đơn hàng' });
  }

  // If already paid
  if (order.status === 'paid' || order.status === 'activated') {
    return res.json({
      order,
      isPaid: true
    });
  }

  // Check live against SePay API
  const livePaid = await checkSePayApiForOrder(order);
  const updatedOrder = db.getPaymentOrder(req.params.orderId) || order;

  res.json({
    order: updatedOrder,
    isPaid: livePaid || updatedOrder.status === 'paid' || updatedOrder.status === 'activated'
  });
});

// POST /api/sepay/webhook - SePay Webhook Endpoint
sepayRouter.post('/webhook', (req, res) => {
  try {
    const payload = req.body;
    console.log('--- SEPAY WEBHOOK RECEIVED ---', payload);

    // SePay payload structure:
    // { id, gateway, transactionDate, accountNumber, subAccount, code, content, transferType, transferAmount, accumulated, referenceCode, description }
    const content = (payload.content || payload.description || payload.code || '').toUpperCase();
    const transferAmount = Number(payload.transferAmount || payload.amount || 0);

    const allOrders = db.getPaymentOrders().filter(o => o.status === 'pending');
    const matchedOrder = allOrders.find(o => {
      return (
        content.includes(o.orderCode.toUpperCase()) ||
        content.includes(o.id.toUpperCase())
      );
    });

    if (matchedOrder) {
      const result = db.activatePaymentOrder(matchedOrder.id, payload.id ? String(payload.id) : undefined);
      console.log(`[SePay Webhook] Activated order ${matchedOrder.orderCode} for teacher ${matchedOrder.teacherEmail}`);
      return res.json({
        success: true,
        message: 'Order activated successfully',
        order: result.order,
        user: result.user
      });
    }

    res.json({ success: true, message: 'Webhook received but no pending order matched' });
  } catch (error: any) {
    console.error('[SePay Webhook Error]', error);
    res.status(500).json({ error: error.message });
  }
});

// POST /api/sepay/manual-confirm/:orderId - Manual confirm trigger
sepayRouter.post('/manual-confirm/:orderId', async (req, res) => {
  const { force } = req.body || {};
  const order = db.getPaymentOrder(req.params.orderId);

  if (!order) {
    return res.status(404).json({ error: 'Không tìm thấy đơn hàng để kích hoạt' });
  }

  if (order.status === 'paid') {
    const user = db.getUserByEmail(order.teacherEmail);
    return res.json({
      success: true,
      message: 'Tài khoản đã được kích hoạt trước đó!',
      user,
      order
    });
  }

  // If force is specified (Admin forced activation)
  if (force) {
    const result = db.activatePaymentOrder(req.params.orderId, `ADMIN_FORCE_${Date.now()}`);
    return res.json({
      success: true,
      message: 'Admin đã kích hoạt đơn hàng thành công!',
      user: result.user,
      order: result.order
    });
  }

  // Check live API
  const livePaid = await checkSePayApiForOrder(order);
  if (livePaid) {
    const activated = db.getPaymentOrder(req.params.orderId);
    const user = db.getUserByEmail(order.teacherEmail);
    return res.json({
      success: true,
      message: 'Giao dịch SePay đã được xác minh thành công! Tài khoản đã được kích hoạt.',
      user,
      order: activated
    });
  }

  // If not found in live API yet, return pending status
  return res.status(202).json({
    success: false,
    pending: true,
    message: `Chưa ghi nhận giao dịch với nội dung "${order.orderCode}" trên SePay. Vui lòng kiểm tra lại nội dung chuyển khoản hoặc chờ ngân hàng xử lý trong 10-30 giây.`
  });
});

// GET /api/sepay/orders - Get all payment orders
sepayRouter.get('/orders', (req, res) => {
  const orders = db.getPaymentOrders();
  res.json(orders);
});
